// The admin assistant: a launcher on every page and a chat with Claude beside the page (a side
// panel on wide screens, a full-screen sheet on phones). Claude reads through the admin API as the
// signed-in staff member; every change it wants to make shows up as a card, and runs only when they
// press Approve. The card leads with what the server found in the database (whose account, how
// much, any text users will read, in full); Claude's own description comes second, as its claim.
// Only staff with assistant.use see any of this.
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  Check,
  CircleX,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  List,
  LoaderCircle,
  MessagesSquare,
  Paperclip,
  Pencil,
  Square,
  SquarePen,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import {
  currentPage,
  setPanel,
  streamAnswer,
  usePanel,
  type ActionView,
  type AssistantStatus,
  type FileRef,
  type Item,
  type StreamEvent,
  type ThreadView,
} from '../lib/assistant';
import { ACCEPT, uploadForAssistant } from '../lib/assistant-files';
import { confirmAction, ErrorNote, Pill, relative, run, ScrollX, toast } from './ui';

const statusQuery = {
  queryKey: ['assistant'],
  queryFn: () => api<AssistantStatus>('/assistant'),
} as const;

/** Waiting approvals across this staff member's conversations, for the launcher. */
const waitingCount = (s: AssistantStatus | undefined) =>
  s?.threads.reduce((n, t) => n + t.waiting, 0) ?? 0;

export function Assistant() {
  const { open } = usePanel();
  const status = useQuery({ ...statusQuery, refetchInterval: open ? 20_000 : 60_000 });
  const waiting = waitingCount(status.data);
  return open ? (
    <Panel status={status.data} statusError={status.error} />
  ) : (
    <button
      type="button"
      onClick={() => setPanel({ open: true })}
      className="fixed bottom-5 right-5 z-30 flex h-12 items-center gap-2 rounded-full border-[1.5px] border-ink bg-paper pl-3.5 pr-4 text-[14px] font-bold shadow-[3px_3px_0_#111110] transition hover:bg-canvas active:translate-x-[2px] active:translate-y-[2px] active:shadow-[1px_1px_0_#111110] max-sm:w-12 max-sm:justify-center max-sm:px-0"
      aria-label={waiting ? `Assistant, ${waiting} waiting for approval` : 'Assistant'}
    >
      <MessagesSquare size={18} strokeWidth={1.75} aria-hidden="true" />
      <span className="max-sm:sr-only">Assistant</span>
      {waiting > 0 && (
        <span
          aria-hidden="true"
          className="grid h-5 min-w-5 place-items-center rounded-full bg-coin px-1.5 text-[11px] font-extrabold tabular-nums text-ink max-sm:absolute max-sm:-right-1 max-sm:-top-1"
        >
          {waiting}
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ Panel */

interface ToolEvent {
  id: string;
  label: string;
  path: string;
  status: 'running' | 'executed' | 'failed';
  note?: string | null;
}
interface Live {
  running: boolean;
  /** The staff member's message, shown until the conversation reloads with it. */
  user: { text: string; files: FileRef[] } | null;
  tools: ToolEvent[];
  text: string;
  error: string | null;
}
const IDLE: Live = { running: false, user: null, tools: [], text: '', error: null };

interface Attachment {
  key: string;
  name: string;
  kind: 'image' | 'text';
  state: 'uploading' | 'ready';
  id?: string;
}

function Panel({
  status,
  statusError,
}: {
  status: AssistantStatus | undefined;
  statusError: unknown;
}) {
  const qc = useQueryClient();
  const { thread: threadId } = usePanel();
  const [view, setView] = useState<'chat' | 'threads'>('chat');
  const [live, setLive] = useState<Live>(IDLE);
  const abort = useRef<AbortController | null>(null);
  const thread = useQuery({
    queryKey: ['assistant-thread', threadId],
    queryFn: () => api<ThreadView>(`/assistant/threads/${threadId}`),
    enabled: !!threadId,
    refetchOnWindowFocus: !live.running,
    refetchInterval: (q) => (!live.running && q.state.data?.waiting ? 30_000 : false),
  });
  // A conversation deleted elsewhere (or expired) falls back to a new one.
  useEffect(() => {
    if (thread.error && (thread.error as { status?: number }).status === 404)
      setPanel({ thread: null });
  }, [thread.error]);

  const ready = !!status?.enabled && status.configured;
  const close = () => setPanel({ open: false });

  // Esc closes, unless a dialog is open on top.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || document.querySelector('[data-dialog-open]')) return;
      if ((e.target as Element | null)?.closest?.('[data-assistant]')) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // On phones the sheet covers the page: keep the page from scrolling underneath.
  useEffect(() => {
    if (window.matchMedia('(min-width: 1024px)').matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /** Stream one answer into the chat, then reload the conversation from the server. */
  async function answer(id: string, path: string, body: unknown, user: Live['user'] = null) {
    const ctrl = new AbortController();
    abort.current = ctrl;
    setLive({ ...IDLE, running: true, user });
    const on = (e: StreamEvent) => {
      if (e.event === 'text') setLive((l) => ({ ...l, text: l.text + e.data.delta }));
      else if (e.event === 'retry') setLive((l) => ({ ...l, text: '' }));
      else if (e.event === 'tool')
        setLive((l) => ({
          ...l,
          tools: [...l.tools.filter((t) => t.id !== e.data.id), e.data],
        }));
      else if (e.event === 'error') setLive((l) => ({ ...l, error: e.data.message }));
    };
    let error: string | null = null;
    try {
      await streamAnswer(path, body, ctrl.signal, on);
    } catch (e) {
      if (!ctrl.signal.aborted) error = (e as Error).message || 'Something went wrong.';
    }
    abort.current = null;
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['assistant-thread', id] }),
      qc.invalidateQueries({ queryKey: ['assistant'] }),
    ]);
    setLive((l) => ({ ...IDLE, error: error ?? l.error }));
  }

  async function send(text: string, files: FileRef[]) {
    let id = threadId;
    if (!id) {
      const r = await run(() => api<{ id: string }>('/assistant/threads', { body: {} }));
      if (!r) return false;
      id = r.id;
      setPanel({ thread: id });
    }
    void answer(
      id,
      `/assistant/threads/${id}/messages`,
      { text, files: files.map((f) => f.id), page: currentPage() },
      { text, files },
    );
    return true;
  }

  async function decide(a: ActionView, how: 'approve' | 'reject') {
    if (!threadId) return;
    const r = await run(() =>
      api<{ action: ActionView; changed: boolean; resume: boolean }>(
        `/assistant/actions/${a.id}/${how}`,
        { body: {} },
      ),
    );
    await qc.invalidateQueries({ queryKey: ['assistant-thread', threadId] });
    if (r && !r.changed && r.action.status === 'expired')
      toast('That change expired: nobody approved it within 15 minutes.', 'error');
    if (r?.resume) await answer(threadId, `/assistant/threads/${threadId}/run`, {});
  }

  // Only changes the server marks as safe to batch (statuses, flags, ticket states): never money,
  // settings, networks, staff or text users read, which are approved one card at a time.
  async function approveAll(batch: ActionView[]) {
    const ok = await confirmAction({
      title: `Approve these ${batch.length} changes?`,
      body: <BatchList actions={batch} />,
      confirmLabel: `Approve ${batch.length}`,
    });
    if (ok === null) return;
    for (const a of batch) await decide(a, 'approve');
  }

  const items = thread.data?.items ?? [];
  const pending = items.flatMap((i) =>
    i.kind === 'action' && i.action.status === 'pending' ? [i.action] : [],
  );
  const batch = pending.filter((a) => a.bulk);
  const title = view === 'threads' ? 'Conversations' : threadId ? thread.data?.thread.title : null;

  return (
    <section
      data-assistant
      aria-label="Assistant"
      className="fixed inset-0 z-50 flex flex-col bg-paper lg:inset-auto lg:bottom-4 lg:right-4 lg:top-4 lg:z-[45] lg:w-[440px] lg:rounded-[20px] lg:border-[1.5px] lg:border-ink lg:shadow-[4px_4px_0_#111110]"
      style={{ animation: 'drawer-in 200ms cubic-bezier(.2,.8,.3,1)' }}
    >
      <header className="flex items-center gap-1 border-b border-hairline py-2.5 pl-4 pr-2">
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-semibold text-stone">Assistant</div>
          <h2 className="truncate text-[16px] font-extrabold tracking-[-0.01em]">
            {title ?? 'New conversation'}
          </h2>
        </div>
        <IconButton
          label={view === 'threads' ? 'Back to the conversation' : 'Conversations'}
          pressed={view === 'threads'}
          disabled={live.running}
          onClick={() => setView(view === 'threads' ? 'chat' : 'threads')}
        >
          <List size={18} />
        </IconButton>
        <IconButton
          label="New conversation"
          disabled={live.running}
          onClick={() => {
            setPanel({ thread: null });
            setView('chat');
            setLive(IDLE);
          }}
        >
          <SquarePen size={17} />
        </IconButton>
        <IconButton label="Close" onClick={close}>
          <X size={18} />
        </IconButton>
      </header>

      {view === 'threads' ? (
        <Threads
          status={status}
          current={threadId}
          onOpen={(id) => {
            setPanel({ thread: id });
            setLive(IDLE);
            setView('chat');
          }}
        />
      ) : (
        <>
          <Conversation
            items={items}
            live={live}
            loading={!!threadId && thread.isLoading}
            error={thread.error && !thread.data ? thread.error : statusError}
            onDecide={decide}
            onSuggest={(text) => void send(text, [])}
            canSuggest={ready && !live.running}
          />
          {pending.length > 1 && !live.running && (
            <div className="flex items-center gap-2 border-t border-hairline bg-canvas px-4 py-2 text-[13px] font-semibold">
              <span className="flex-1">
                {pending.length} changes wait for you
                {batch.length > 1 && batch.length < pending.length && (
                  <span className="block text-[12px] font-medium text-stone">
                    The others need their own approval.
                  </span>
                )}
              </span>
              {batch.length > 1 && (
                <button
                  type="button"
                  className="btn-primary min-h-9 px-3 py-1.5 text-[13px]"
                  onClick={() => void approveAll(batch)}
                >
                  Review {batch.length} together
                </button>
              )}
            </div>
          )}
          {status && !ready ? (
            <p className="border-t border-hairline px-4 py-4 text-[14px] text-graphite">
              {!status.enabled
                ? 'The assistant is switched off. An admin can turn it on in Settings → Support and fraud assistants.'
                : 'The assistant needs ANTHROPIC_API_KEY before it can answer.'}
            </p>
          ) : (
            <Composer
              limits={status?.limits}
              running={live.running}
              onSend={send}
              onStop={() => abort.current?.abort()}
            />
          )}
        </>
      )}
    </section>
  );
}

function IconButton({
  label,
  onClick,
  children,
  disabled,
  pressed,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      className={`grid h-10 w-10 shrink-0 place-items-center rounded-[10px] transition hover:bg-canvas hover:text-ink disabled:opacity-40 ${pressed ? 'bg-canvas text-ink' : 'text-stone'}`}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ Conversation */

function suggestions(): string[] {
  const { ids } = currentPage();
  if (ids.withdrawalId)
    return ['Why was this cash-out held?', 'Is this user’s earning history normal?'];
  if (ids.ticketId)
    return ['Summarise this ticket and what the user needs', 'Check this user’s recent cash-outs'];
  if (ids.userId)
    return ['What has this user earned in the last week?', 'Anything risky about this account?'];
  return ['What needs attention today?', 'Which cash-outs are on hold, and why?'];
}

function Conversation({
  items,
  live,
  loading,
  error,
  onDecide,
  onSuggest,
  canSuggest,
}: {
  items: Item[];
  live: Live;
  loading: boolean;
  error: unknown;
  onDecide: (a: ActionView, how: 'approve' | 'reject') => Promise<void>;
  onSuggest: (text: string) => void;
  canSuggest: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  });
  const empty = !items.length && !live.user && !loading;
  return (
    <div
      ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget;
        stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      }}
      className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"
    >
      {error ? <ErrorNote error={error} /> : null}
      {loading && (
        <div className="space-y-3" role="status" aria-label="Loading">
          <div className="ml-auto h-10 w-2/3 animate-pulse rounded-[18px] bg-ink/[0.07]" />
          <div className="h-16 w-5/6 animate-pulse rounded-[12px] bg-ink/[0.05]" />
        </div>
      )}
      {empty && (
        <div className="pb-2 pt-6">
          <p className="text-[20px] font-extrabold leading-tight tracking-[-0.02em]">
            Ask about anything in the admin.
          </p>
          <p className="mt-2 text-[14px] leading-relaxed text-graphite">
            Claude looks things up as you, with your permissions. When something should change, it
            shows you the exact request first; nothing changes until you approve it.
          </p>
          <div className="mt-5 grid gap-2">
            {suggestions().map((s) => (
              <button
                key={s}
                type="button"
                disabled={!canSuggest}
                onClick={() => onSuggest(s)}
                className="rounded-[12px] border border-hairline px-3.5 py-2.5 text-left text-[14px] font-semibold transition hover:border-ink disabled:opacity-50"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      <ol className="space-y-4" aria-label="Conversation">
        {groupReads(items).map((g) =>
          g.kind === 'reads' ? (
            <li key={g.id} className="space-y-1.5">
              {g.actions.map((a) => (
                <ReadRow
                  key={a.id}
                  label={a.label}
                  path={a.path}
                  status={a.status === 'executed' ? 'executed' : 'failed'}
                  note={a.note}
                />
              ))}
            </li>
          ) : (
            <li key={g.item.id}>
              <ItemView item={g.item} onDecide={onDecide} />
            </li>
          ),
        )}
        {live.user && (
          <li>
            <UserBubble text={live.user.text} files={live.user.files} />
          </li>
        )}
        {live.tools.length > 0 && (
          <li className="space-y-1.5">
            {live.tools.map((t) => (
              <ReadRow key={t.id} {...t} />
            ))}
          </li>
        )}
        {live.text && (
          <li>
            <Answer text={live.text} />
          </li>
        )}
      </ol>
      <div role="status" aria-live="polite" className="mt-3">
        {live.running && !live.text && (
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-stone">
            <LoaderCircle size={14} className="animate-spin" aria-hidden="true" />
            {live.tools.some((t) => t.status === 'running') ? 'Looking it up…' : 'Thinking…'}
          </span>
        )}
      </div>
      {live.error && (
        <div className="mt-3">
          <ErrorNote error={new Error(live.error)} />
        </div>
      )}
    </div>
  );
}

/** Reads next to each other show as one quiet list. */
function groupReads(items: Item[]) {
  const out: (
    { kind: 'reads'; id: string; actions: ActionView[] } | { kind: 'item'; item: Item }
  )[] = [];
  for (const item of items) {
    const read = item.kind === 'action' && item.action.risk !== 'critical';
    const last = out.at(-1);
    if (read && last?.kind === 'reads') last.actions.push(item.action);
    else if (read) out.push({ kind: 'reads', id: item.id, actions: [item.action] });
    else out.push({ kind: 'item', item });
  }
  return out;
}

function ItemView({
  item,
  onDecide,
}: {
  item: Item;
  onDecide: (a: ActionView, how: 'approve' | 'reject') => Promise<void>;
}) {
  if (item.kind === 'user')
    return <UserBubble text={item.text} files={item.files} page={item.page?.path} />;
  if (item.kind === 'assistant') return <Answer text={item.text} />;
  return <ChangeCard action={item.action} onDecide={onDecide} />;
}

function UserBubble({
  text,
  files,
  page,
}: {
  text: string;
  files: FileRef[];
  page?: string | undefined;
}) {
  return (
    <div className="flex flex-col items-end">
      <div className="max-w-[88%] whitespace-pre-wrap break-words rounded-[18px] rounded-tr-[6px] bg-ink px-3.5 py-2.5 text-[14px] leading-relaxed text-white">
        {text}
        {files.length > 0 && (
          <span className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f) => (
              <span
                key={f.id}
                className="inline-flex max-w-full items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[12px] font-semibold"
              >
                <FileIcon file={f} />
                <span className="truncate">{f.name}</span>
              </span>
            ))}
          </span>
        )}
      </div>
      {page && page !== '/' && (
        <span className="mt-1 max-w-[88%] truncate text-[12px] text-stone">On {page}</span>
      )}
    </div>
  );
}

function FileIcon({ file }: { file: { name: string; kind: 'image' | 'text' } }) {
  const Icon =
    file.kind === 'image'
      ? ImageIcon
      : /\.(csv|xlsx)/i.test(file.name)
        ? FileSpreadsheet
        : FileText;
  return <Icon size={12} aria-hidden="true" className="shrink-0" />;
}

function ReadRow({
  label,
  path,
  status,
  note,
}: {
  label: string;
  path: string;
  status: 'running' | 'executed' | 'failed';
  note?: string | null | undefined;
}) {
  return (
    <div className="flex items-start gap-2 text-[13px] leading-snug">
      <span className="mt-[1px] shrink-0">
        {status === 'running' ? (
          <LoaderCircle size={14} className="animate-spin text-stone" aria-label="Running" />
        ) : status === 'executed' ? (
          <Check size={14} className="text-mint-deep" aria-label="Done" />
        ) : (
          <CircleX size={14} className="text-berry-deep" aria-label="Didn’t work" />
        )}
      </span>
      <span className="min-w-0">
        <span className="font-semibold text-graphite">{label}</span>{' '}
        <code className="text-[12px] text-stone [overflow-wrap:anywhere]">
          <Path path={path} />
        </code>
        {status === 'failed' && note && (
          <span className="block text-[12px] text-berry-deep">{note}</span>
        )}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ Changes */

/** Re-render every 30 seconds (expiry countdowns). */
function useMinuteTick() {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
}

const OUTCOME: Record<
  Exclude<ActionView['status'], 'pending'>,
  { label: string; tone: 'mint' | 'berry' | 'neutral' | 'sky' }
> = {
  approved: { label: 'Running', tone: 'sky' },
  executed: { label: 'Approved · done', tone: 'mint' },
  failed: { label: 'Didn’t work', tone: 'berry' },
  rejected: { label: 'Rejected', tone: 'neutral' },
  expired: { label: 'Expired', tone: 'neutral' },
};

/** A change Claude asked for: the approval card while it waits, then its outcome. */
function ChangeCard({
  action: a,
  onDecide,
}: {
  action: ActionView;
  onDecide: (a: ActionView, how: 'approve' | 'reject') => Promise<void>;
}) {
  useMinuteTick();
  const waiting = a.status === 'pending' && (a.expiresAt ?? 0) > Date.now();
  if (!waiting) {
    const o = a.status === 'pending' ? OUTCOME.expired : OUTCOME[a.status];
    return (
      <div className="rounded-[14px] border border-hairline px-3.5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone={o.tone}>{o.label}</Pill>
          <span className="text-[14px] font-bold">{a.label}</span>
        </div>
        {a.effect && (
          <p className="mt-1.5 text-[13px] leading-snug text-graphite">
            <Effect text={a.effect} />
          </p>
        )}
        {a.status === 'failed' && a.note && (
          <p className="mt-1 text-[13px] font-semibold text-berry-deep">{a.note}</p>
        )}
        <details className="mt-1.5 text-[12px] text-stone">
          <summary className="cursor-pointer select-none font-semibold hover:text-ink">
            Request
          </summary>
          <RequestDetails action={a} />
        </details>
      </div>
    );
  }
  const minutes = Math.max(1, Math.ceil(((a.expiresAt ?? 0) - Date.now()) / 60_000));
  return (
    <div
      className="rounded-[16px] border-[1.5px] border-ink bg-paper shadow-[3px_3px_0_#111110]"
      role="group"
      aria-label={`Waiting for your approval: ${a.label}`}
    >
      <div className="flex items-center gap-2 px-4 pt-3.5">
        <Pill tone="sun">Needs your approval</Pill>
        <span className="text-[12px] font-semibold text-stone">
          {minutes === 1 ? 'Expires in a minute' : `Expires in ${minutes} min`}
        </span>
      </div>
      <div className="px-4 pb-4 pt-2">
        <h3 className="text-[15px] font-bold leading-snug">{a.label}</h3>
        <div className="mt-2.5 rounded-[12px] bg-canvas px-3 py-2.5">
          <div className="text-[12px] font-semibold text-stone">
            What will run, as you (checked by the server)
          </div>
          {a.effect && (
            <p className="mt-0.5 text-[14px] font-semibold leading-snug [overflow-wrap:anywhere]">
              <Effect text={a.effect} />
            </p>
          )}
          <Facts facts={a.facts} />
          <details className="mt-2 text-[12px] text-stone">
            <summary className="cursor-pointer select-none font-semibold hover:text-ink">
              Exact request
            </summary>
            <RequestDetails action={a} />
          </details>
        </div>
        {a.summary && (
          <div className="mt-2.5 border-l-2 border-hairline pl-3">
            <div className="text-[12px] font-semibold text-stone">
              Claude’s description (its claim, not checked)
            </div>
            <p className="mt-0.5 text-[13px] leading-relaxed text-graphite [overflow-wrap:anywhere]">
              {a.summary}
            </p>
          </div>
        )}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost min-h-10 px-4 py-2 text-[14px]"
            onClick={() => void onDecide(a, 'reject')}
          >
            Reject
          </button>
          <button
            type="button"
            className="btn-primary min-h-10 px-4 py-2 text-[14px]"
            onClick={() => void onDecide(a, 'approve')}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What to check before approving, in full and never cut: amounts in the money colour, text users
 * will read as a quote, and the server's warnings in red.
 */
function Facts({ facts }: { facts: ActionView['facts'] }) {
  if (!facts.length) return null;
  return (
    <dl className="mt-2 space-y-2">
      {facts.map((f, i) =>
        f.tone === 'warn' ? (
          <div
            key={i}
            className="flex gap-1.5 text-[13px] font-semibold leading-snug text-berry-deep"
          >
            <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <dt className="inline">{f.label}: </dt>
              <dd className="inline whitespace-pre-wrap [overflow-wrap:anywhere]">{f.value}</dd>
            </div>
          </div>
        ) : (
          <div key={i}>
            <dt className="text-[12px] font-semibold text-stone">{f.label}</dt>
            {f.tone === 'text' ? (
              <dd className="mt-0.5 whitespace-pre-wrap rounded-[8px] border border-hairline bg-paper px-2.5 py-2 text-[13px] leading-relaxed text-ink [overflow-wrap:anywhere]">
                {f.value}
              </dd>
            ) : (
              <dd
                className={`whitespace-pre-wrap text-[13px] leading-snug [overflow-wrap:anywhere] ${
                  f.tone === 'money' ? 'font-bold tabular-nums text-coin-ink' : 'text-graphite'
                }`}
              >
                {f.value}
              </dd>
            )}
          </div>
        ),
      )}
    </dl>
  );
}

/** Approving several at once: every change listed in the server's words, and how many of each. */
function BatchList({ actions }: { actions: ActionView[] }) {
  const counts = new Map<string, number>();
  for (const a of actions) counts.set(a.label, (counts.get(a.label) ?? 0) + 1);
  return (
    <div className="space-y-3 text-left">
      <p className="text-[14px] font-semibold">
        {[...counts].map(([label, n]) => `${n} × ${label}`).join(' · ')}
      </p>
      <ol className="max-h-[50vh] list-decimal space-y-2 overflow-y-auto pl-5 text-[13px] leading-snug">
        {actions.map((a) => (
          <li key={a.id} className="[overflow-wrap:anywhere]">
            {a.effect ?? a.label}
            {a.facts.map((f, i) => (
              <span key={i} className="block text-stone">
                {f.label}: {f.value}
              </span>
            ))}
          </li>
        ))}
      </ol>
      <p className="text-[13px] text-graphite">
        Each runs as you, one after another, exactly as listed. Money, settings, staff and anything
        users read are never in a batch.
      </p>
    </div>
  );
}

/** The change in code's words, with coin amounts in the money colour. */
function Effect({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\d[\d,]* coins)/).map((p, i) =>
        i % 2 ? (
          <span key={i} className="font-bold text-coin-ink tabular-nums">
            {p}
          </span>
        ) : (
          <Fragment key={i}>{p}</Fragment>
        ),
      )}
    </>
  );
}

/** A path that wraps after its slashes and separators, not mid-word. */
function Path({ path }: { path: string }) {
  return (
    <>
      {path.split(/(?<=[/?&])/).map((p, i) => (
        <Fragment key={i}>
          {i > 0 && <wbr />}
          {p}
        </Fragment>
      ))}
    </>
  );
}

function RequestDetails({ action: a }: { action: ActionView }) {
  const body =
    a.body && typeof a.body === 'object' && Object.keys(a.body).length
      ? JSON.stringify(a.body, null, 2)
      : null;
  return (
    <>
      <code className="mt-1.5 block text-[12px] text-graphite [overflow-wrap:anywhere]">
        <span className="font-bold text-ink">{a.method}</span> <Path path={a.path} />
      </code>
      {body && (
        <pre className="mt-1.5 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-[8px] border border-hairline bg-paper px-2.5 py-2 text-[12px] leading-relaxed text-graphite">
          {body}
        </pre>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ Claude's text */

/** Claude's answer: paragraphs, lists, tables, **bold** and `code`; never HTML. */
function Answer({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const parts = text.split(/```[^\n]*\n?/);
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      blocks.push(
        <pre
          key={`c${i}`}
          className="overflow-x-auto rounded-[10px] bg-canvas px-3 py-2 text-[12px] leading-relaxed"
        >
          {part.replace(/\n$/, '')}
        </pre>,
      );
      return;
    }
    for (const [j, chunk] of part.split(/\n{2,}/).entries()) {
      const lines = chunk.split('\n').filter((l) => l.trim());
      if (!lines.length) continue;
      const key = `${i}-${j}`;
      if (lines.every((l) => /^\s*[-*•] /.test(l)))
        blocks.push(
          <ul key={key} className="list-disc space-y-1 pl-5">
            {lines.map((l, k) => (
              <li key={k}>{inline(l.replace(/^\s*[-*•] /, ''))}</li>
            ))}
          </ul>,
        );
      else if (lines.every((l) => /^\s*\d+[.)] /.test(l)))
        blocks.push(
          <ol key={key} className="list-decimal space-y-1 pl-5">
            {lines.map((l, k) => (
              <li key={k}>{inline(l.replace(/^\s*\d+[.)] /, ''))}</li>
            ))}
          </ol>,
        );
      else if (lines.length > 1 && lines.every((l) => l.trim().startsWith('|')))
        blocks.push(<Table key={key} lines={lines} />);
      else
        blocks.push(
          <p key={key} className="whitespace-pre-wrap">
            {lines.map((l, k) => (
              <Fragment key={k}>
                {k > 0 && '\n'}
                {/^#{1,4} /.test(l) ? <strong>{inline(l.replace(/^#+ /, ''))}</strong> : inline(l)}
              </Fragment>
            ))}
          </p>,
        );
    }
  });
  return <div className="space-y-2.5 break-words text-[14px] leading-relaxed">{blocks}</div>;
}

function inline(s: string): ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*|`[^`]+`)/).map((p, i) =>
    p.startsWith('**') && p.endsWith('**') && p.length > 4 ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : p.startsWith('`') && p.endsWith('`') && p.length > 2 ? (
      <code key={i} className="rounded-[5px] bg-canvas px-1 py-0.5 text-[12px]">
        {p.slice(1, -1)}
      </code>
    ) : (
      <Fragment key={i}>{p}</Fragment>
    ),
  );
}

function Table({ lines }: { lines: string[] }) {
  const cells = (l: string) =>
    l
      .trim()
      .replace(/^\||\|$/g, '')
      .split('|')
      .map((c) => c.trim());
  const rows = lines.filter((l) => !/^\s*\|?\s*:?-{2,}/.test(l)).map(cells);
  const [head, ...body] = rows;
  // Columns of numbers (coins, dollars, counts) line up on the right.
  const numeric = head!.map(
    (_, k) => body.length > 0 && body.every((r) => /^[-+]?[$]?[\d,.]+%?$|^—?$/.test(r[k] ?? '')),
  );
  return (
    <ScrollX className="rounded-[10px] border border-hairline">
      <table className="data-table text-[13px]">
        <thead>
          <tr>
            {head!.map((c, i) => (
              <th key={i} className={numeric[i] ? 'text-right' : ''}>
                {inline(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i}>
              {r.map((c, k) => (
                <td key={k} className={numeric[k] ? 'text-right' : ''}>
                  {inline(c)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollX>
  );
}

/* ------------------------------------------------------------------ Composer */

function Composer({
  limits,
  running,
  onSend,
  onStop,
}: {
  limits: AssistantStatus['limits'] | undefined;
  running: boolean;
  onSend: (text: string, files: FileRef[]) => Promise<boolean>;
  onStop: () => void;
}) {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  // Grow with the text, up to about eight lines.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  function add(list: FileList | File[]) {
    if (!limits) return;
    const room = limits.filesPerMessage - files.length;
    const picked = Array.from(list);
    if (picked.length > room)
      toast(`At most ${limits.filesPerMessage} files in one message.`, 'error');
    for (const file of picked.slice(0, Math.max(0, room))) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      const kind = /^image\//.test(file.type) ? 'image' : 'text';
      setFiles((f) => [...f, { key, name: file.name, kind, state: 'uploading' }]);
      uploadForAssistant(file, limits).then(
        (r) =>
          setFiles((f) =>
            f.map((x) => (x.key === key ? { ...x, ...r, state: 'ready' as const } : x)),
          ),
        (e: Error) => {
          setFiles((f) => f.filter((x) => x.key !== key));
          toast(e.message || `${file.name} couldn’t be attached.`, 'error');
        },
      );
    }
  }

  const uploading = files.some((f) => f.state === 'uploading');
  const canSend = !running && !uploading && text.trim().length > 0;
  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSend) return;
    const ready = files.flatMap((f) =>
      f.id ? [{ id: f.id, name: f.name, kind: f.kind } satisfies FileRef] : [],
    );
    const sent = await onSend(text.trim(), ready);
    if (sent) {
      setText('');
      setFiles([]);
    }
  }
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) add(e.dataTransfer.files);
  };

  return (
    <form
      onSubmit={submit}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={onDrop}
      className="relative border-t border-hairline px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-3"
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-2 z-10 grid place-items-center rounded-[14px] border-2 border-dashed border-ink bg-paper/95 text-[14px] font-bold">
          Drop to attach
        </div>
      )}
      {files.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5" aria-label="Attached files">
          {files.map((f) => (
            <li
              key={f.key}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-hairline bg-canvas py-1 pl-2.5 pr-1 text-[12px] font-semibold"
            >
              {f.state === 'uploading' ? (
                <LoaderCircle size={12} className="animate-spin" aria-label="Uploading" />
              ) : (
                <FileIcon file={f} />
              )}
              <span className="max-w-[200px] truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => setFiles((x) => x.filter((y) => y.key !== f.key))}
                className="grid h-6 w-6 place-items-center rounded-full text-stone hover:bg-paper hover:text-ink"
                aria-label={`Remove ${f.name}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-end gap-1.5 rounded-[14px] border border-hairline bg-paper p-1.5 transition focus-within:border-ink">
        <button
          type="button"
          onClick={() => picker.current?.click()}
          disabled={!limits || files.length >= (limits?.filesPerMessage ?? 0)}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-stone transition hover:bg-canvas hover:text-ink disabled:opacity-40"
          aria-label="Attach files"
          title="Attach images, Markdown, text, CSV or Excel"
        >
          <Paperclip size={17} />
        </button>
        <input
          ref={picker}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files) add(e.target.files);
            e.target.value = '';
          }}
        />
        <textarea
          ref={input}
          rows={1}
          value={text}
          maxLength={limits?.messageChars ?? 8000}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void submit();
            }
          }}
          onPaste={(e) => {
            if (e.clipboardData.files.length) {
              e.preventDefault();
              add(e.clipboardData.files);
            }
          }}
          placeholder="Ask, or drop a screenshot or a list"
          aria-label="Message to the assistant"
          className="max-h-[200px] min-h-9 flex-1 resize-none bg-transparent px-1 py-[6px] text-[16px] leading-snug text-ink outline-none placeholder:text-stone sm:py-[7px] sm:text-[14px]"
        />
        {running ? (
          <button
            type="button"
            onClick={onStop}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] border border-hairline text-ink transition hover:border-ink"
            aria-label="Stop"
            title="Stop"
          >
            <Square size={13} fill="currentColor" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-ink text-white transition hover:bg-[#2a2926] disabled:opacity-30"
            aria-label="Send"
            title="Send"
          >
            <ArrowUp size={17} />
          </button>
        )}
      </div>
      <p className="mt-1.5 px-1 text-[12px] text-stone">
        Claude acts as you. Changes wait for your approval.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------ Threads */

function Threads({
  status,
  current,
  onOpen,
}: {
  status: AssistantStatus | undefined;
  current: string | null;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['assistant'] });
  async function rename(id: string) {
    const title = name.trim();
    setEditing(null);
    if (!title) return;
    await run(() => api(`/assistant/threads/${id}`, { method: 'PATCH', body: { title } }));
    await Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['assistant-thread', id] })]);
  }
  async function remove(id: string, title: string) {
    const ok = await confirmAction({
      title: 'Delete this conversation?',
      body: `“${title}”, its files and any changes still waiting are deleted. Changes that already ran stay in the audit log.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok === null) return;
    await run(() => api(`/assistant/threads/${id}`, { method: 'DELETE', body: {} }), 'Deleted.');
    if (id === current) setPanel({ thread: null });
    await refresh();
  }
  const list = status?.threads ?? [];
  if (!list.length)
    return (
      <p className="flex-1 px-4 py-8 text-center text-[14px] text-stone">
        No conversations yet. They’re kept for 30 days after you last use them.
      </p>
    );
  return (
    <ul className="flex-1 divide-y divide-hairline overflow-y-auto overscroll-contain">
      {list.map((t) => (
        <li
          key={t.id}
          className={`group flex items-center gap-1 pr-2 ${t.id === current ? 'bg-canvas' : ''}`}
        >
          {editing === t.id ? (
            <form
              className="flex-1 px-3 py-2"
              onSubmit={(e) => {
                e.preventDefault();
                void rename(t.id);
              }}
            >
              <input
                autoFocus
                className="input min-h-9 py-1.5 text-[14px]"
                value={name}
                maxLength={80}
                aria-label="Conversation name"
                onChange={(e) => setName(e.target.value)}
                onBlur={() => void rename(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    setEditing(null);
                  }
                }}
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => onOpen(t.id)}
              className="min-w-0 flex-1 px-4 py-3 text-left"
              aria-current={t.id === current ? 'true' : undefined}
            >
              <span className="block truncate text-[14px] font-bold">{t.title}</span>
              <span className="mt-0.5 flex items-center gap-2 text-[12px] text-stone">
                {relative(t.updatedAt)}
                {t.waiting > 0 && <Pill tone="sun">{t.waiting} waiting</Pill>}
              </span>
            </button>
          )}
          {editing !== t.id && (
            <>
              <IconButton
                label={`Rename “${t.title}”`}
                onClick={() => {
                  setName(t.title);
                  setEditing(t.id);
                }}
              >
                <Pencil size={15} />
              </IconButton>
              <IconButton label={`Delete “${t.title}”`} onClick={() => void remove(t.id, t.title)}>
                <Trash2 size={15} />
              </IconButton>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
